import { Collapse, List, ListItemButton, ListItemText } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { PermissionBoundary } from '../../../../../../core/permissions/permission-boundary.component';
import { NavigationItem } from '../../navigation-bar.component';
import { NavigationMenuBoundary } from '../../navigation-menu-boundary.component';
import { collectNavigationUrls } from '../../navigation-item.utils';
import { useNavMenuMobileItemStyles } from '../nav-menu-mobile-item/nav-menu-mobile-item.styles';

/** Each level of nesting indents its items by this much more. */
const INDENT_PER_DEPTH_PX = 16;

const indentFor = (depth: number) => (depth > 0 ? { paddingLeft: `${16 + depth * INDENT_PER_DEPTH_PX}px` } : undefined);

/** A menu in the drawer. A nested menu expands in place, indented beneath its parent, since touch has no hover. */
export function NavMenuCollapsibleMobileItem({
  item,
  onClick,
  depth = 0
}: {
  item: NavigationItem;
  onClick(...args: any): void;
  depth?: number;
}) {
  const match = useRouteMatch({ path: collectNavigationUrls(item.children), exact: true });
  const [isExpanded, setIsExpanded] = useState(false);
  const isSelected = match?.isExact && !isExpanded;

  const classes = useNavMenuMobileItemStyles({ isSelected });

  return (
    <NavigationMenuBoundary item={item}>
      <ListItemButton
        className={classes.menuItemsMobile__button}
        disableTouchRipple={isSelected}
        onClick={() => setIsExpanded(!isExpanded)}
        style={indentFor(depth)}
      >
        <ListItemText>{item.text}</ListItemText>
        {isExpanded ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {(item.children || []).map((child) =>
            Array.isArray(child.children) ? (
              <NavMenuCollapsibleMobileItem key={child.text} item={child} onClick={onClick} depth={depth + 1} />
            ) : (
              <CollapsibleMobileMenuItemChild key={child.text} child={child} onClick={onClick} depth={depth + 1} />
            )
          )}
        </List>
      </Collapse>
    </NavigationMenuBoundary>
  );
}

function CollapsibleMobileMenuItemChild({
  child,
  onClick,
  depth
}: {
  child: NavigationItem;
  onClick(...args: any): void;
  depth: number;
}) {
  const match = useRouteMatch({ path: child.url, exact: true });
  const classes = useNavMenuMobileItemStyles({ isSelected: match?.isExact });
  return (
    <PermissionBoundary permissions={child.permissions} availableUiFeatures={child.availableUiFeatures}>
      <ListItemButton
        className={classes.menuItemsMobile__button}
        {...(child.url ? { component: Link, to: child.url } : {})}
        onClick={onClick}
        style={indentFor(depth)}
      >
        <ListItemText primary={child.text} />
      </ListItemButton>
    </PermissionBoundary>
  );
}
